import axios from 'axios';
import { phoneAuthService } from './phoneAuth';
import { API_CONFIG } from '../config/apiConfig';

interface FirebaseAuthResponse {
  success: boolean;
  data?: any;
  error?: string;
  message?: string;
}

class FirebaseApiService {
  private async getAuthHeaders(): Promise<{ [key: string]: string }> {
    const idToken = await phoneAuthService.getIdToken();
    
    if (!idToken) {
      throw new Error('Firebase token not available');
    }

    return {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Verify phone authentication - gửi userId và idToken để map user
   */
  async verifyPhoneAuth(userId: string): Promise<FirebaseAuthResponse> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/firebase-auth/verify-phone`,
        { userId }, // Gửi userId để map với Firebase user
        { headers }
      );

      return response.data;
    } catch (error: any) {
      console.error('Verify phone auth error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Verification failed'
      };
    }
  }

  /**
   * Unlink Firebase phone authentication
   */
  async unlinkPhoneAuth(userId: string): Promise<FirebaseAuthResponse> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/firebase-auth/unlink-phone`,
        {},
        { headers }
      );

      return response.data;
    } catch (error: any) {
      console.error('Unlink phone auth error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Unlink failed'
      };
    }
  }

  /**
   * Get Firebase user info
   */
  async getFirebaseUserInfo(): Promise<FirebaseAuthResponse> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await axios.get(
        `${API_CONFIG.BASE_URL}/firebase-auth/user-info`,
        { headers }
      );

      return response.data;
    } catch (error: any) {
      console.error('Get Firebase user info error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get user info'
      };
    }
  }

  /**
   * Link existing user với Firebase UID
   */
  async linkUserAccount(kilohubUserId: string): Promise<FirebaseAuthResponse> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/firebase-auth/link-user`,
        { kilohubUserId },
        { headers }
      );

      return response.data;
    } catch (error: any) {
      console.error('Link user account error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to link account'
      };
    }
  }

  /**
   * Check if Firebase token is available
   */
  async hasValidToken(): Promise<boolean> {
    try {
      const idToken = await phoneAuthService.getIdToken();
      return !!idToken;
    } catch (error) {
      return false;
    }
  }
}

export const firebaseApiService = new FirebaseApiService();
